import { makeStyles } from '@material-ui/core';
import { max, min } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { ArrowRight } from 'react-feather';
import { useNavigate } from 'react-router';
import { usePrefixTranslation } from '../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { enumToIcon, enumToStatusType, projectStatus } from '../../../types/common';
import { ProjectSummary } from '../../../types/project';
import { motions, StatusIcon, Stepper } from '../../global';
import { AwaitingReport } from './AwaitingReport';
import { Completed } from './Completed';
import { EditorScheduler } from './EditorScheduler';
import { InProgress } from './InProgress';
import { NoProject } from './NoProject';
import { ReadyToInspect } from './ReadyToInspect';
import { UnderReview } from './UnderReview';

const useProjectEditorClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        padding: '30px 30px 20px 30px',
    },
    stepper: {
        marginBottom: 30,
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
});

export const ProjectEditor = (props: { summary: ProjectSummary; schedule?: boolean; onClose: () => void }) => {
    const classes = useProjectEditorClasses();
    const navigate = useNavigate();
    const { t } = usePrefixTranslation();
    const dispatch = useTypedDispatch();
    const { project } = useTypedSelector(state => ({ project: state.project.changed }));
    const [view, setView] = React.useState<'no project' | 'project' | 'scheduler'>(
        props.summary.plan == undefined
            ? 'no project'
            : props.schedule
            ? 'scheduler'
            : props.summary.inspection != undefined && props.summary.inspection !== -1
            ? 'project'
            : 'no project',
    );

    React.useEffect(() => {
        dispatch(actions.asset.get(props.summary.code));
        if (props.summary.plan != undefined) dispatch(actions.plan.get(props.summary.plan));
    }, []);

    return (
        <div className={classes.root}>
            <Header view={view} />
            <AnimatePresence exitBeforeEnter>
                <motion.div key={view} className={classes.content} {...motions.presence({ slide: 10 }, true)}>
                    {view === 'no project' ? (
                        <NoProject
                            onCreatePlan={() => navigate('/plans/create')}
                            onStartProject={() => (dispatch(actions.project.create()), setView('scheduler'))}
                        />
                    ) : view === 'scheduler' ? (
                        <EditorScheduler
                            onComplete={() => setView((project?.id ?? -1) !== -1 ? 'project' : 'no project')}
                        />
                    ) : !project ? (
                        <span>{'#project not in store'}</span>
                    ) : (
                        <>
                            <Stepper
                                className={classes.stepper}
                                labels={projectStatus.map(s => t(`common:status.${s}`))}
                                current={t(`status.${project.status}`)}
                            />
                            <AnimatePresence exitBeforeEnter>
                                <motion.div
                                    key={project.status}
                                    className={classes.section}
                                    {...motions.presence({ slide: 10 }, true)}
                                >
                                    {project.status === 'ready to inspect' ? (
                                        <ReadyToInspect onSchedule={() => setView('scheduler')} />
                                    ) : project.status === 'in progress' ? (
                                        <InProgress />
                                    ) : project.status === 'awaiting report' ? (
                                        <AwaitingReport />
                                    ) : project.status === 'under review' ? (
                                        <UnderReview />
                                    ) : (
                                        <Completed onClose={props.onClose} />
                                    )}
                                </motion.div>
                            </AnimatePresence>
                        </>
                    )}
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

const useHeaderClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        padding: '20px 30px 10px 30px',
        boxShadow: '0 3px 20px -5px rgba(28, 31, 52, 0.2)',
        color: theme.colors.darkBlue[5],
        '& > span:first-of-type': {
            ...theme.typography.content.caption.paragraph,
        },
        '& > span:last-of-type': {
            ...theme.typography.headings.h1,
        },
    },
    status: {
        '& > span': {
            textTransform: 'capitalize',
        },
    },
});

const Header = (props: { view: 'no project' | 'project' | 'scheduler' }) => {
    const classes = useHeaderClasses();
    const { t, Trans } = usePrefixTranslation('project', { project: 'editor.ProjectEditor' });
    const { asset, plan, project } = useTypedSelector(state => ({
        asset: state.asset.changed,
        plan: state.plan.changed,
        project: state.project.changed,
    }));
    const status = project?.status ?? (plan ? 'planned' : 'not-planned');
    const statusType = enumToStatusType[status];
    const statusIcon = enumToIcon[status];
    const minDueDate = min([plan?.external_due_date ?? 0, plan?.internal_due_date ?? 0]);
    const maxDueDate = max([plan?.external_due_date ?? 0, plan?.internal_due_date ?? 0]);
    const due = plan == undefined ? new Date(0) : +minDueDate !== 0 ? minDueDate : maxDueDate;
    const start = project?.estimated_start_date;
    const end = project?.estimated_completed_date;
    const scheduling = props.view === 'scheduler';

    if (!asset) return <span>{'#asset not in store'}</span>;

    return (
        <div className={classes.root}>
            <span>{`${asset?.code} | ${!scheduling ? asset?.type?.name ?? '#no type defined' : asset?.name}`}</span>
            <span>{!scheduling ? asset?.name : t(`header-title-schedule`)}</span>
            <StatusIcon className={classes.status} type={statusType} icon={statusIcon} colored>
                <Trans
                    i18nKey={`header-status-${project != undefined ? 'project' : status}`}
                    values={{ due, status, start, end }}
                    components={{ arrow: <ArrowRight style={{ width: 15, height: 15 }} /> }}
                />
            </StatusIcon>
        </div>
    );
};
